import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import { DataTable } from "ui-components/data-table";
import Button, { ButtonSizes, ButtonThemes } from "ui-components/button/button";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import StatusPill from "ui-components/status-pill/statusPill";
import TimestampDisplay from "ui-components/timestamp-display/timestampDisplay";
import DockUserCard from "ui-components/dock-user-card/dockUserCard";
import EquipmentCard from "ui-components/equipment-card/equipmentCard";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import { Routes } from "app/route/RoutesConfig";
import { formatSessionDuration } from "app/data/dock-scan/dockLogMappers";
import { userSelector } from "slices/user/userSlice";
import {
  useDockActiveSessions,
  useDockAuthEvents,
  useDockEmployee,
  useDockScanLogs,
  useDockScanMutations,
  useDockTags,
} from "../hooks/useDockScanQueries";
import {
  DOCK_SCAN_LOGS_LIST_COLUMN_OPTIONS,
  DOCK_SCAN_LOGS_LIST_COLUMNS_HIDDEN_BY_DEFAULT,
  getDockScanLogsListColumns,
} from "../dockScanLogsListColumns";
import {
  DOCK_AUTH_AUDIT_LIST_COLUMN_OPTIONS,
  getDockAuthAuditListColumns,
} from "../dockAuthAuditListColumns";
import { ScanCountTags } from "../dockScanLogColumns";
import DockEmployeeTagSelect, {
  resolveTagIdsFromSelections,
  toTagSelectOptions,
} from "./DockEmployeeTagSelect";

type SidebarTab = "details" | "scanLogs" | "loginHistory";

interface DockEmployeeSidebarProps {
  show: boolean;
  employeeId?: string;
  terminalNumber: number;
  onClose: () => void;
}

const editSchema = Yup.object({
  email: Yup.string().email("Invalid email").required("Email is required"),
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
});

const DockEmployeeSidebar: React.FC<DockEmployeeSidebarProps> = ({
  show,
  employeeId,
  terminalNumber,
  onClose,
}) => {
  const history = useHistory();
  const userState = useSelector(userSelector);
  const terminalName = userState.activeTerminal?.name;
  const terminalAirportCode = userState.activeTerminal?.shortName;
  const terminalPath = userState.activeTerminal?.id ? `/${userState.activeTerminal.id}` : "";

  const { data: employee, isLoading } = useDockEmployee(show ? employeeId : undefined);
  const { data: tags } = useDockTags(terminalNumber);
  const { data: activeSessions } = useDockActiveSessions(terminalNumber);
  const { data: scanLogsData } = useDockScanLogs(
    { employeeId, terminalNumber, take: 10 },
    !!employeeId && show
  );
  const { data: authEventsData } = useDockAuthEvents(
    { employeeId, take: 10 },
    !!employeeId && show
  );
  const { updateEmployee, deactivateEmployee, createTag } = useDockScanMutations(terminalNumber);
  const [error, setError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [activeTab, setActiveTab] = useState<SidebarTab>("details");
  const formDirtyRef = useRef(false);

  const activeSession = useMemo(
    () => activeSessions?.find((s) => s.employeeId === employeeId),
    [activeSessions, employeeId]
  );

  const scanLogColumns = useMemo(
    () => getDockScanLogsListColumns({ showEmployee: false }),
    []
  );
  const authColumns = useMemo(
    () =>
      getDockAuthAuditListColumns({
        terminalNumber,
        terminalName,
        terminalAirportCode,
        showEmployee: false,
      }),
    [terminalNumber, terminalName, terminalAirportCode]
  );

  const [scanColumnVisibility, setScanColumnVisibility] = useState<Record<string, boolean>>(() => {
    const hidden = new Set([
      ...DOCK_SCAN_LOGS_LIST_COLUMNS_HIDDEN_BY_DEFAULT,
      "employeeId",
    ]);
    return DOCK_SCAN_LOGS_LIST_COLUMN_OPTIONS.reduce(
      (acc, { id }) => ({ ...acc, [id]: !hidden.has(id) }),
      {} as Record<string, boolean>
    );
  });

  const [authColumnVisibility, setAuthColumnVisibility] = useState<Record<string, boolean>>(() => {
    const hidden = new Set(["employeeId"]);
    return DOCK_AUTH_AUDIT_LIST_COLUMN_OPTIONS.reduce(
      (acc, { id }) => ({ ...acc, [id]: !hidden.has(id) }),
      {} as Record<string, boolean>
    );
  });

  useEffect(() => {
    if (!show) {
      setError(null);
      setConfirmClose(false);
      setActiveTab("details");
      formDirtyRef.current = false;
    }
  }, [show]);

  const handleClose = (dirty?: boolean) => {
    if ((dirty || formDirtyRef.current) && !confirmClose) {
      setConfirmClose(true);
      return;
    }
    setConfirmClose(false);
    onClose();
  };

  const scanCount = scanLogsData?.items?.length ?? 0;
  const authCount = authEventsData?.items?.length ?? 0;

  const goToScanLogs = () => {
    if (!employeeId) return;
    history.push(
      `${terminalPath}${Routes.dockScanLogs.list}?employeeId=${encodeURIComponent(employeeId)}`
    );
  };

  const goToAuthAudit = () => {
    if (!employeeId) return;
    history.push(
      `${terminalPath}${Routes.dockAuthAudit.list}?employeeId=${encodeURIComponent(employeeId)}`
    );
  };

  const initialValues = employee
    ? {
        email: employee.email,
        firstName: employee.firstName,
        lastName: employee.lastName,
        tags: toTagSelectOptions(employee.tags) as XGSSelectOption[],
      }
    : { email: "", firstName: "", lastName: "", tags: [] as XGSSelectOption[] };

  return (
    <SlideOutSidebar
      show={show}
      onClose={() => handleClose()}
      header="Dock User details"
      size="medium"
      spinner={isLoading}
    >
      {confirmClose && (
        <div className="dock-users__unsaved-banner">
          You have unsaved changes.
          <div className="dock-users__unsaved-actions">
            <Button theme={ButtonThemes.gray} size={ButtonSizes.small} onClick={() => setConfirmClose(false)}>
              Keep Editing
            </Button>
            <Button theme={ButtonThemes.red} size={ButtonSizes.small} onClick={onClose}>
              Discard
            </Button>
          </div>
        </div>
      )}

      {employee && (
        <>
          <div className="dock-users__sidebar-header">
            <DockUserCard
              employeeId={employee.employeeId}
              firstName={employee.firstName}
              lastName={employee.lastName}
              email={employee.email}
              status={employee.status}
              tags={employee.tags}
              terminalNumber={employee.terminalNumber}
              isOnDock={!!activeSession}
              updatedAt={employee.updatedAt}
              session={
                activeSession
                  ? {
                      equipmentId: activeSession.equipmentId,
                      sessionDurationMs: activeSession.sessionDurationMs,
                      scansTotal: activeSession.scans.total,
                      scansFailure: activeSession.scans.failure,
                    }
                  : undefined
              }
              linkToProfile={false}
              showHoverCard={false}
              className="dock-users__sidebar-header-card"
            />
            <div className="dock-users__sidebar-header-pills">
              <StatusPill
                label={employee.status}
                variant={employee.status === "ACTIVE" ? "success" : "inactive"}
              />
              <StatusPill
                label={activeSession ? "ON DOCK" : "OFF DOCK"}
                variant={activeSession ? "live" : "neutral"}
              />
            </div>
          </div>

          <div className="dock-users__sidebar-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "details"}
              className={`dock-users__sidebar-tab${activeTab === "details" ? " dock-users__sidebar-tab--active" : ""}`}
              onClick={() => setActiveTab("details")}
            >
              User Details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "scanLogs"}
              className={`dock-users__sidebar-tab${activeTab === "scanLogs" ? " dock-users__sidebar-tab--active" : ""}`}
              onClick={() => setActiveTab("scanLogs")}
            >
              Scan Logs
              <span className="dock-users__sidebar-tab-badge">{scanCount}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "loginHistory"}
              className={`dock-users__sidebar-tab${activeTab === "loginHistory" ? " dock-users__sidebar-tab--active" : ""}`}
              onClick={() => setActiveTab("loginHistory")}
            >
              Login History
              <span className="dock-users__sidebar-tab-badge">{authCount}</span>
            </button>
          </div>

          {activeTab === "details" && (
            <div className="dock-users__sidebar-tab-panel" role="tabpanel">
              {activeSession && (
                <div className="dock-users__sidebar-section">
                  <h4>Current Session</h4>
                  <div className="dock-users__session-card">
                    <div>
                      Equipment:{" "}
                      {activeSession.equipmentId ? (
                        <EquipmentCard
                          unitNumber={activeSession.equipmentId}
                          keepTooltipInside={false}
                        />
                      ) : (
                        <strong>—</strong>
                      )}
                    </div>
                    <div>Duration: {formatSessionDuration(activeSession.sessionDurationMs)}</div>
                    <div>
                      Started:{" "}
                      <TimestampDisplay value={activeSession.assignmentStartedAt} format="relative" />
                    </div>
                    <div className="dock-users__session-scans">
                      <span>Scans:</span>
                      <ScanCountTags
                        total={activeSession.scans.total}
                        success={activeSession.scans.success}
                        failure={activeSession.scans.failure}
                      />
                    </div>
                    {activeSession.activeWorkflow && (
                      <div className="dock-users__workflow-next">
                        Next: {activeSession.activeWorkflow.expectedScanType || "—"}
                        {activeSession.activeWorkflow.expectedDoorId &&
                          ` · Door D${activeSession.activeWorkflow.expectedDoorId}`}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={editSchema}
                onSubmit={async (values) => {
                  setError(null);
                  try {
                    const tagIds = await resolveTagIdsFromSelections(
                      values.tags,
                      tags || [],
                      (name) => createTag.mutateAsync({ terminalNumber, name })
                    );
                    await updateEmployee.mutateAsync({
                      employeeId: employee.employeeId,
                      body: {
                        email: values.email,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        tagIds,
                      },
                    });
                    formDirtyRef.current = false;
                    onClose();
                  } catch (e) {
                    setError((e as Error).message);
                  }
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  dirty,
                  isSubmitting,
                  setFieldValue,
                }) => {
                  formDirtyRef.current = dirty;
                  return (
                    <Form className="dock-users__sidebar-form">
                      {dirty && (
                        <div className="dock-users__dirty-notice">You have unsaved changes</div>
                      )}
                      <div className="dock-users__name-row">
                        <LabeledTextInput
                          name="firstName"
                          labelMode={LabelModes.column}
                          label="First Name"
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.firstName && errors.firstName ? errors.firstName : undefined}
                        />
                        <LabeledTextInput
                          name="lastName"
                          labelMode={LabelModes.column}
                          label="Last Name"
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.lastName && errors.lastName ? errors.lastName : undefined}
                        />
                      </div>
                      <LabeledTextInput
                        name="email"
                        labelMode={LabelModes.column}
                        label="Email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && errors.email ? errors.email : undefined}
                      />
                      <div className="dock-users__tag-picker">
                        <DockEmployeeTagSelect
                          tags={tags || []}
                          value={values.tags}
                          onChange={(options) => setFieldValue("tags", options)}
                          disabled={isSubmitting || updateEmployee.isLoading || createTag.isLoading}
                        />
                      </div>

                      {error && <XGSErrorMessage>{error}</XGSErrorMessage>}

                      <div className="dock-users__sidebar-actions">
                        <Button
                          type="submit"
                          theme={ButtonThemes.blue}
                          size={ButtonSizes.medium}
                          disabled={!dirty || isSubmitting || updateEmployee.isLoading || createTag.isLoading}
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          theme={ButtonThemes.gray}
                          size={ButtonSizes.medium}
                          onClick={() => handleClose(dirty)}
                        >
                          Cancel
                        </Button>
                        {employee.status === "ACTIVE" ? (
                          <Button
                            type="button"
                            theme={ButtonThemes.red}
                            size={ButtonSizes.medium}
                            onClick={async () => {
                              setError(null);
                              try {
                                await deactivateEmployee.mutateAsync(employee.employeeId);
                                onClose();
                              } catch (e) {
                                setError((e as Error).message);
                              }
                            }}
                            disabled={deactivateEmployee.isLoading}
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            theme={ButtonThemes.blueInverted}
                            size={ButtonSizes.medium}
                            onClick={async () => {
                              setError(null);
                              try {
                                await updateEmployee.mutateAsync({
                                  employeeId: employee.employeeId,
                                  body: { status: "ACTIVE" },
                                });
                                onClose();
                              } catch (e) {
                                setError((e as Error).message);
                              }
                            }}
                          >
                            Reactivate
                          </Button>
                        )}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          )}

          {activeTab === "scanLogs" && (
            <div className="dock-users__sidebar-tab-panel" role="tabpanel">
              {(scanLogsData?.items || []).length === 0 ? (
                <div className="text-light">No recent scans</div>
              ) : (
                <DataTable
                  columns={scanLogColumns}
                  data={scanLogsData?.items || []}
                  rowHeight={52}
                  responsive
                  shrinkToContent
                  columnVisibility={scanColumnVisibility}
                  onColumnVisibilityChange={setScanColumnVisibility}
                  listHeightOverride={Math.min(360, (scanLogsData?.items?.length || 1) * 52 + 48)}
                />
              )}
              <div className="dock-users__view-more">
                <button type="button" className="dock-users__view-more-link" onClick={goToScanLogs}>
                  View More
                </button>
              </div>
            </div>
          )}

          {activeTab === "loginHistory" && (
            <div className="dock-users__sidebar-tab-panel" role="tabpanel">
              {(authEventsData?.items || []).length === 0 ? (
                <div className="text-light">No login events</div>
              ) : (
                <DataTable
                  columns={authColumns}
                  data={authEventsData?.items || []}
                  rowHeight={52}
                  responsive
                  shrinkToContent
                  columnVisibility={authColumnVisibility}
                  onColumnVisibilityChange={setAuthColumnVisibility}
                  listHeightOverride={Math.min(360, (authEventsData?.items?.length || 1) * 52 + 48)}
                />
              )}
              <div className="dock-users__view-more">
                <button type="button" className="dock-users__view-more-link" onClick={goToAuthAudit}>
                  View More
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </SlideOutSidebar>
  );
};

export default DockEmployeeSidebar;
